import * as React from 'react';
import { Step, Stepper, StepLabel } from 'material-ui/Stepper';
import RaisedButton from 'material-ui/RaisedButton';
import AccountCreation from './account.creation';
import BusinessRegister from '../business/register';
import {
  SignupState,
  AccountType,
  AccountCreationRequest,
  AccountCreationError,
  BusinessRegisterRequest
} from './signup.types';
import { CLIENT } from './signup.constants';
import { AuthState } from '../auth/auth.types';
import RefreshIndicator from 'material-ui/RefreshIndicator';
import { ADMIN } from '../account/Constants';
import { CreateUpdateRequestParameters } from '../business/business.types';
import { AddressInfo, AddressInfoError } from '../geo/address.types';
import { DEFAULT_COUNTRY_CODE } from '../geo/geo.const';
import * as moment from 'moment-timezone';
import { M3PLUS } from '../business/business.constants';

const ACCOUNT_CREATION_STEP_INDEX = 0;
const BUSINESS_REGISTRATION_STEP_INDEX = 1;
const DEFAULT_POINT_PER_ATTENDANCE = 0;

type Props = {
  readonly checkEmailAvailability: (email: string) => Promise<Response>
  readonly authState: AuthState
  readonly toDashboard: () => void
  readonly toLogin: () => void
  readonly toAdminDashboard: () => void
  readonly signupState: SignupState
  readonly createAccount: (request: AccountCreationRequest) => Promise<Response>
  readonly registerBusiness: (request: CreateUpdateRequestParameters) => Promise<Response>
};

type States = {
  readonly loading: boolean
  readonly finished: boolean
  readonly stepIndex: number
  readonly accountType: AccountType
  readonly accountCreationRequest: AccountCreationRequest
  readonly accountCreationError: AccountCreationError
  readonly createAccountClicked: boolean
  readonly registerBusinessClicked: boolean
  readonly businessRegisterRequest: BusinessRegisterRequest
  readonly addressInfoError: AddressInfoError
  readonly businessLogoImageFile?: File
  readonly fileSizeError: string
};

class SignupStepperComponent extends React.Component<Props, States> {
  
  constructor(props: Props) {
    super(props);

    this.state = {
      loading: false,
      finished: false,
      businessLogoImageFile: undefined,
      stepIndex: ACCOUNT_CREATION_STEP_INDEX,
      accountType: CLIENT,
      addressInfoError: {
        addressError: '',
        cityError: '',
        countryError: '',
        provinceError: '',
        postalCodeError: '',
        phoneError: '',
        mobileError: ''
      },
      accountCreationRequest: {
        email: '',
        phone: '',
        firstName: '',
        lastName: '',
        password: '',
        confirmPassword: ''
      },
      accountCreationError: {
        emailError: '',
        firstNameError: '',
        lastNameError: '',
        phoneError: '',
        passwordError: '',
        confirmPasswordError: ''
      },
      businessRegisterRequest: {
        name: '',
        servicePackage: M3PLUS,
        addressInfo: {
          address: '',
          city: '',
          country: DEFAULT_COUNTRY_CODE,
          province: '',
          postalCode: '',
          phone: '',
          mobile: undefined
        }
      },
      createAccountClicked: false,
      registerBusinessClicked: false,
      fileSizeError: ''
    };

    this.handleNext = this.handleNext.bind(this);
    this.getStepContent = this.getStepContent.bind(this);
    this.incrementStepIndex = this.incrementStepIndex.bind(this);
    this.selectDashboard = this.selectDashboard.bind(this);
  }

  componentWillReceiveProps(nextProps: Props) {
    if (this.props !== nextProps) {
      this.setState(
        {
          accountCreationError: {
            ...this.state.accountCreationError,
            emailError: this.isEmailTaken(nextProps) ? 'The email is taken' : ''
          }
        },
        () => {
          this.setPhoneAndMobileErrorMessage();
          this.incrementStepIndex(nextProps);
        }
      );
    }
  }
  
  render() {
    const { stepIndex, loading } = this.state;
    const contentStyle = {margin: '0 16px'};

    return (
      <div 
        style={{
          width: '100%', 
          maxWidth: 700, 
          margin: 'auto'
        }} 
        className="bg-light mt-2 mb-2 pt-2 pb-2"
      >
        {this.renderSteps()}
        <div style={contentStyle}>
          <div>
            {this.getStepContent(stepIndex)}
            {
              loading ?
              <div className="mb-5">
                <RefreshIndicator
                  size={40}
                  left={10}
                  top={0}
                  status="loading"
                  style={{
                    display: 'inline-block',
                    position: 'relative',
                  }}
                />
              </div> :
              <div className="mb-5">
                <div className="row">
                  <div className="col-1">
                    <RaisedButton
                      label={this.pickLabel(stepIndex)}
                      primary={true}
                      onClick={this.handleNext}
                      disabled={
                        this.state.stepIndex === BUSINESS_REGISTRATION_STEP_INDEX ?
                        this.isBusinessRegisterRequestMissing() :
                        false
                      }
                    />
                  </div>
                </div>
              </div>
            }
          </div>
        </div>
      </div>
    );
  }

  private incrementStepIndex = (nextProps: Props): void => {
    const { stepIndex, accountType, createAccountClicked, registerBusinessClicked } = this.state;
    const { signupState } = nextProps;

    if (
      signupState.statusCode === 200 &&
      !this.isThereAccountCreationError() &&
      !this.isAccountCreationRequestMissing() &&
      createAccountClicked
    ) {
      return this.setState(
        {loading: false, createAccountClicked: false},
        () => this.setState(
          {
            stepIndex: accountType === ADMIN ? stepIndex + 2 : stepIndex + 1,
            finished: stepIndex >= BUSINESS_REGISTRATION_STEP_INDEX
          }
        )
      );
    } else if (
      signupState.statusCode === 200 &&
      !this.isThereBusinessRegisterError() &&
      !this.isBusinessRegisterRequestMissing() &&
      registerBusinessClicked
    ) {
      return this.setState(
        {loading: false, registerBusinessClicked: false},
        () => this.setState(
          {
            stepIndex: accountType === ADMIN ? stepIndex : stepIndex + 1,
            finished: stepIndex >= BUSINESS_REGISTRATION_STEP_INDEX
          }
        )
      );
    } else {
      return this.setState({loading: false});
    }
  }

  private pickLabel = (stepIndex: number): string => {
    switch (stepIndex) {
      case ACCOUNT_CREATION_STEP_INDEX:
        return 'CREATE';
      case BUSINESS_REGISTRATION_STEP_INDEX:
        return 'REGISTER';
      default:
        return 'DASHBOARD';
    }
  }

  private handleNext = (): void => {
    const { stepIndex } = this.state;

    switch (stepIndex) {
      case ACCOUNT_CREATION_STEP_INDEX:
        this.setState(
          {loading: true, createAccountClicked: true},
          () => this.props.createAccount(this.state.accountCreationRequest)
        );
        break;
      case BUSINESS_REGISTRATION_STEP_INDEX:
        this.setState(
          {loading: true, registerBusinessClicked: true},
          () => this.props.registerBusiness(this.createBusinessRegisterRequest())
        );
        break;
      default:
        this.setState(
          {loading: true},
          this.props.toAdminDashboard
        );
        break;
    }
  }

  private createBusinessRegisterRequest = (): CreateUpdateRequestParameters => {
    const { signupState } = this.props;
    const { businessRegisterRequest, businessLogoImageFile } = this.state;
    return {
      clientId: signupState.account.id,
      name: businessRegisterRequest.name,
      address: businessRegisterRequest.addressInfo.address,
      city: businessRegisterRequest.addressInfo.city,
      province: businessRegisterRequest.addressInfo.province,
      country: businessRegisterRequest.addressInfo.country,
      postalCode: businessRegisterRequest.addressInfo.postalCode,
      phone: businessRegisterRequest.addressInfo.phone,
      mobile: businessRegisterRequest.addressInfo.mobile,
      logoFile: businessLogoImageFile,
      servicePackage: businessRegisterRequest.servicePackage,
      timeZone: moment.tz.guess(),
      pointPerAttendance: DEFAULT_POINT_PER_ATTENDANCE,
      tags: [],
      failedCheckinNotificationCcEmails: [],
      failedCheckinNotificationCcPhoneNumbers: [],
      failedCheckinEmail: false,
      failedCheckinSms: false,
      smsDelay: 0,
      timeBasedMultipleCheckin: false,
      sendCheckinNotificationToMobile2: false
    };
  }

  private getStepContent = (stepIndex: number) => {
    switch (stepIndex) {
      case ACCOUNT_CREATION_STEP_INDEX:
        return(
          <AccountCreation
            whichAccount={this.whichAccount}
            accountCreationRequestHandler={this.accountCreationRequestHandler}
            accountCreationError={this.state.accountCreationError}
            accountCreationErrorHandler={this.accountCreationErrorHandler}
            checkEmailAvailability={this.props.checkEmailAvailability}
          />
        );
      case BUSINESS_REGISTRATION_STEP_INDEX:
        return (
          <BusinessRegister
            businessRegisterRequest={this.state.businessRegisterRequest}
            businessRegisterRequestHandler={this.businessRegisterRequestHandler}
            addressInfo={this.state.businessRegisterRequest.addressInfo}
            addressInfoHandler={this.addressInfoHandler}
            addressInfoError={this.state.addressInfoError}
            onBusinessLogoImageFileHandler={this.onBusinessLogoImageFileHandler}
            fileSizeError={this.state.fileSizeError}
          />
        );
      default:
        return 'Finished!';
    }
  }

  private businessRegisterRequestHandler = (request: BusinessRegisterRequest): void =>
    this.setState({businessRegisterRequest: request})

  private selectDashboard = (signupState: SignupState): void => {
    const { toDashboard, toAdminDashboard } = this.props;

    if (signupState.account && signupState.account.accountType === ADMIN) {
      toAdminDashboard();
    } else {
      toDashboard();
    }
  }

  private whichAccount = (accountType: AccountType) => this.setState({accountType: accountType});

  private renderSteps = (): JSX.Element => {
    const { stepIndex, accountType } = this.state;
    if (accountType === CLIENT) {
      return(
        <Stepper activeStep={stepIndex}>
          <Step>
            <StepLabel>Account Creation</StepLabel>
          </Step>
          <Step>
            <StepLabel>Register Business</StepLabel>
          </Step>
        </Stepper>
      );
    } else {
      return(
        <Stepper activeStep={stepIndex}>
          <Step>
            <StepLabel>Account Creation</StepLabel>
          </Step>
        </Stepper>
      );
    }
  }

  private accountCreationRequestHandler = (request: AccountCreationRequest): void =>
    this.setState({accountCreationRequest: request})

  private accountCreationErrorHandler = (error: AccountCreationError): void =>
    this.setState({accountCreationError: error})

  private isThereAccountCreationError = (): boolean => {
    const {
      emailError, firstNameError, lastNameError, phoneError, passwordError, confirmPasswordError
    } = this.state.accountCreationError;

    return emailError !== '' || firstNameError !== '' || lastNameError !== '' || phoneError !== '' ||
      passwordError !== '' || confirmPasswordError !== '';
  }

  private isThereBusinessRegisterError = (): boolean => {
    const {
      addressError, provinceError, cityError, phoneError, mobileError, postalCodeError
    } = this.state.addressInfoError;

    return addressError !== '' || provinceError !== '' || cityError !== '' || mobileError !== '' ||
      phoneError !== '' || postalCodeError !== '';
  }

  private isEmailTaken = (nextProps: Props): boolean => {
    const { emailValidationStatus } = nextProps.signupState.validation;
    return emailValidationStatus !== undefined && emailValidationStatus === 409;
  }

  private isAccountCreationRequestMissing = () => {
    const { email, phone, firstName, lastName, password, confirmPassword } = this.state.accountCreationRequest;
    return email === '' || phone === '' || firstName === '' || lastName === '' || password === '' ||
      confirmPassword === '';
  }

  private isBusinessRegisterRequestMissing = () => {
    const { name, addressInfo } = this.state.businessRegisterRequest;
    return name === '' || addressInfo.address === '' || addressInfo.city === '' || addressInfo.country === '' ||
      addressInfo.phone === '' || addressInfo.postalCode === '' || addressInfo.province === '' ||
      addressInfo.mobile === undefined;
  }

  private addressInfoHandler = (addressInfo: AddressInfo): void => 
    this.setState({
      businessRegisterRequest: {
        ...this.state.businessRegisterRequest,
        addressInfo: addressInfo
      }
    })

  private onBusinessLogoImageFileHandler = (
    fileFormatError: string, 
    fileSizeError: string, 
    newImageFile: File
  ): void => {
    this.setState({
      businessLogoImageFile: newImageFile,
      fileSizeError: fileSizeError
    });
  }

  private setPhoneAndMobileErrorMessage = (): void => {
    const { phoneNumberValidationStatus } = this.props.signupState.validation;

    this.setState({
      addressInfoError: {
        ...this.state.addressInfoError,
        phoneError: phoneNumberValidationStatus && phoneNumberValidationStatus === 409 ?
          'The phone number is already taken' :
          ''
    }});
  }
}
  
export default SignupStepperComponent;



// WEBPACK FOOTER //
// ./src/admin/signup.stepper.component.tsx